/**
 * React Query keys.
 *
 * Every key carries the startup id, and every per-analysis key carries the
 * analysis type. Without both, one startup's Porter report and another's
 * Competition report share a cache entry — which is a cross-tenant data leak
 * that looks like a rendering bug.
 *
 * Report keys also carry the BOUND VERSION IDS rather than "latest". A report is
 * a statement about specific inputs, so caching it under a moving selector would
 * let a founder's new setup version silently change what an investor is looking
 * at without anything re-fetching.
 */

export const strategicAnalysisKeys = {
  all: ['strategic-analysis'] as const,

  /** Public methodology metadata. Not startup-scoped: it describes the method. */
  manifest: () => [...strategicAnalysisKeys.all, 'manifest'] as const,

  startup: (startupId: string) => [...strategicAnalysisKeys.all, 'startup', startupId] as const,

  /** The latest run per analysis. What the report screen polls. */
  run: (startupId: string, analysisType: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'run', analysisType] as const,

  /** Server-assembled candidate draft. Changes whenever a source changes. */
  contextDraft: (startupId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'context-draft'] as const,

  /** A SPECIFIC approved version — the one a report was bound to. */
  contextVersion: (startupId: string, versionId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'context', versionId] as const,

  /** The newest approved version, for the editor's starting point. */
  latestContext: (startupId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'context', 'latest'] as const,

  marketAssumptions: (startupId: string, versionId: string | 'latest') =>
    [...strategicAnalysisKeys.startup(startupId), 'assumptions', versionId] as const,

  exitThesis: (startupId: string, versionId: string | 'latest') =>
    [...strategicAnalysisKeys.startup(startupId), 'thesis', versionId] as const,

  /** Claim-level evidence for one run. */
  evidence: (startupId: string, runId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'evidence', runId] as const,

  /** Statuses and fingerprints of every run, for the profile entry card. */
  runStates: (startupId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'run-states'] as const,

  /** What today's approved inputs WOULD fingerprint to, per analysis. */
  inputFingerprints: (startupId: string) =>
    [...strategicAnalysisKeys.startup(startupId), 'input-fingerprints'] as const,
} as const;

/**
 * Keys to invalidate after a new context version is approved.
 *
 * Reports are NOT invalidated. A new context version makes an existing report
 * STALE, not wrong — it is still a true record of what was assessed under the
 * inputs it names, and refetching it would return the same row anyway. The
 * staleness flag comes from comparing fingerprints at read time, and refreshing
 * is an explicit founder action rather than a cache side effect.
 */
export function keysAfterContextSave(startupId: string) {
  return [
    strategicAnalysisKeys.contextDraft(startupId),
    strategicAnalysisKeys.latestContext(startupId),
    // The fingerprints DID change — a new approved version is exactly what makes
    // existing reports stale. Leaving them cached is how a report keeps claiming
    // to be current after the founder changed the inputs under it. This
    // invalidates the staleness SIGNAL, not the reports themselves.
    strategicAnalysisKeys.inputFingerprints(startupId),
  ];
}

/**
 * Keys to invalidate after a new bound VERSION is saved.
 *
 * `contextDraft` is the one that was missing, and its absence is what made both
 * bound-input analyses unreachable. The draft carries two things a save
 * changes: the per-analysis READINESS — computed server-side from the latest
 * accepted assumptions and thesis — and the VERSION IDS that Generate binds. A
 * founder who saved their assumptions kept the pre-save draft, so Market
 * Validation went on reporting the same block and the Generate button went on
 * offering no version to bind. The screen was correct about a cache that was
 * three seconds out of date.
 *
 * `runStates` follows for the same reason: the profile entry card reads
 * readiness out of it and would keep showing "needs input" after the input was
 * given.
 */
export function keysAfterThesisSave(startupId: string) {
  return [
    strategicAnalysisKeys.exitThesis(startupId, 'latest'),
    strategicAnalysisKeys.inputFingerprints(startupId),
    strategicAnalysisKeys.contextDraft(startupId),
    strategicAnalysisKeys.runStates(startupId),
  ];
}

export function keysAfterAssumptionsSave(startupId: string) {
  return [
    strategicAnalysisKeys.marketAssumptions(startupId, 'latest'),
    strategicAnalysisKeys.inputFingerprints(startupId),
    strategicAnalysisKeys.contextDraft(startupId),
    strategicAnalysisKeys.runStates(startupId),
  ];
}
